/** Privacy policy and terms, in plain words. */
import { APP_NAME, PLANS, STUDENT_DISCOUNT } from "@brainlog/shared";
import { PublicFooter, PublicHeader } from "./PublicLayout";

function Page({ title, updated, children }: { title: string; updated: string; children: React.ReactNode }) {
  return (
    <div className="min-h-full bg-background">
      <PublicHeader />
      <main className="mx-auto max-w-3xl px-5 py-12">
        <h1 className="font-display text-4xl font-semibold">{title}</h1>
        <p className="mt-2 text-sm text-muted-foreground">Last updated: {updated}</p>
        <div className="mt-8 space-y-8 text-[0.95rem] leading-relaxed [&_h2]:font-display [&_h2]:text-xl [&_h2]:font-semibold [&_li]:ml-5 [&_li]:list-disc [&_p]:mt-2 [&_ul]:mt-2 [&_ul]:space-y-1">
          {children}
        </div>
      </main>
      <PublicFooter />
    </div>
  );
}

export function PrivacyPage() {
  return (
    <Page title="Privacy policy" updated="October 2026">
      <section>
        <h2>In short</h2>
        <ul>
          <li>No ads, no third-party trackers, no selling or sharing of your data.</li>
          <li>We count visits only if you accept the analytics cookie, and those counts stay with us.</li>
          <li>Your notes are visible only to you and the people you share them with.</li>
          <li>AI only sees what you send it, when you ask, through the AI service you chose.</li>
          <li>You can export everything, and delete it, at any time.</li>
        </ul>
      </section>
      <section>
        <h2>What we keep</h2>
        <ul>
          <li><strong>Your account:</strong> name, email address, and your password as a secure hash (never the password itself).</li>
          <li><strong>Your work:</strong> notebooks, projects, notes, papers, files, tasks and emails you add. This is the service.</li>
          <li><strong>Connections you set up:</strong> AI keys and Zotero or Mendeley keys are stored encrypted and used only for what you asked.</li>
          <li><strong>Sign-in sessions:</strong> when and from which browser you signed in, so you stay signed in and can be signed out.</li>
          <li><strong>Payments</strong> are handled by Stripe; card details never reach {APP_NAME}.</li>
          <li>
            <strong>Emails to you:</strong> account emails (confirming your address, resetting your password, payments) always. News and offers a few times a year at
            most, and only if you did not stop them: every such email has a link to stop them in one click, and you can switch them off in Settings → Account. We keep
            which of these emails were sent to you, not whether you opened them.
          </li>
          <li>
            <strong>Use of the service:</strong> for each AI request, which action, which AI model, whether your own key or the one included in your plan was used, its
            size (tokens) and its cost — never the text sent or received. We use this to apply your plan's allowance, check costs and improve {APP_NAME}. We also keep
            simple counts per account (number of notebooks and notes, space used, last sign-in).
          </li>
          <li>
            <strong>How AI results are received:</strong> whether a result was saved, changed before saving or not saved, and how long that took. If you answer “Was this
            useful?”, your answer and comment (we read them to improve {APP_NAME}; never the result itself). For questions to the assistant and to “Ask the project”,
            only the kind of question (for example “Tasks and deadlines”, chosen by the AI that answers) and whether it could be answered, never the question.
          </li>
          <li>
            <strong>Invitations:</strong> if you join with a colleague's link, we keep who invited you, to give you both the extra AI credits. They see that someone joined
            with their link and your name in the email that tells them.
          </li>
        </ul>
      </section>
      <section>
        <h2>Who else sees your data</h2>
        <ul>
          <li><strong>The people you share with:</strong> members of a notebook or project can read and edit it.</li>
          <li>
            <strong>AI services, only when you use AI:</strong> the text needed for the request (for example a paper you ask to read) goes to the AI service of your choice
            (your own key) or the one included in your plan (OpenAI or Anthropic, depending on the action and the model you pick; if one fails, the other is
            tried). Before it goes to an external AI service, TypeSafe's AI checks the text for hidden instructions aimed at the AI and BrainLog leaves those
            passages out. You can switch AI off for any notebook or project. If someone in a project turns on its <em>Knowledge base</em>, the text of each new item
            of that project (note, task, file, paper, email…) goes to the smallest AI model to be summarised in two lines, until it is turned off.
          </li>
          <li>
            <strong>Backups:</strong> every night, a copy of the service's data is stored with Infomaniak (Swiss Backup, in Switzerland). It is encrypted on our server
            before it leaves, so Infomaniak cannot read it. Copies are deleted after two months at most.
          </li>
          <li>
            <strong>Sorting emails by importance</strong>, only if it is turned on in a notebook's or project's Correspondence: the sender, the subject and the beginning
            of each email go to TypeSafe's AI, which rates how important it is. It writes and sends nothing.
          </li>
          <li>
            <strong>GitHub, GitLab, Codeberg, Hugging Face and Zenodo</strong> are asked for the README and description of the repositories and datasets you add to a
            project's Code and data, and, for repository reports, their public list of files, licence and releases.
          </li>
          <li>
            <strong>OpenAlex</strong> receives the DOIs and titles of papers you add, to fill in their details and find free PDFs; with a project's literature watch on,
            also the topics it watches.
          </li>
          <li><strong>Zotero or Mendeley</strong>, if you connect them, exchange the references you choose to sync.</li>
          <li>
            <strong>Overleaf</strong>, when you click <em>Open in Overleaf</em>, fetches that manuscript's LaTeX files from a link that works for 10 minutes. A{" "}
            <strong>Git repository you connect</strong> to a manuscript (an Overleaf project, GitHub, GitLab, Codeberg…) receives its LaTeX files each time someone clicks{" "}
            <em>Send</em>, as a commit under their name; the token is stored encrypted and can be removed with <em>Disconnect</em>.
          </li>
          <li>
            <strong>Email briefings</strong>, only if you turn them on: we email you at your account address through our email provider (Infomaniak, in Switzerland). Each
            email has a link that stops them in one click.
          </li>
        </ul>
        <p>
          <strong>Services we do not run.</strong> Some tools you may connect are run by other companies, under their own terms and privacy policies. BrainLog does not
          manage them and cannot control what they keep:
        </p>
        <ul>
          <li>
            <strong>Telegram</strong>, if you connect it in Settings → Assistant: the briefings we send you there (titles, two-line summaries, links), what you send the bot
            (questions, notes, DOIs, links) and its answers pass through Telegram's servers. Your questions are answered with AI from your notebooks and projects (as for
            other AI actions), and the conversation is kept in BrainLog for 7 days so the assistant can follow it (<em>/reset</em> forgets it at once). Encrypted notebooks
            and projects, and those where outside services are turned off, are never read or shown there. Disconnect at any time (<em>/stop</em> in Telegram, or Settings → Assistant).
          </li>
          <li>
            <strong>AI tools you connect</strong> (Claude, ChatGPT, Mistral Le Chat, Hermes or another AI app that can connect to other apps through MCP) act as you, in the notebooks and projects you
            allow; what they read goes to the company that runs them. They can never delete, never reach encrypted notebooks and projects or those where outside services are turned off,
            and you can disconnect them in Settings → AI.
          </li>
          <li>
            <strong>Your own AI key</strong>, if you add one: your requests go to that AI company under your own account with it.
          </li>
          <li>
            <strong>Web pages of a project</strong>, if its owner makes one: anyone with the link (and its password, if there is one) can read the parts the owner chose, as
            they are now, until the end date or until the owner stops sharing. Search engines are asked not to list these pages. We count the visits, and keep nothing
            about the visitors.
          </li>
        </ul>
      </section>
      <section>
        <h2>Locked (encrypted) notebooks</h2>
        <p>
          A notebook, project or single note can be end-to-end encrypted: it is locked and unlocked in your browser, and our server only stores unreadable data. Not even we
          can read it. Because of that, search, AI and some other features don't work inside it. If you lose both your passphrase and your recovery key, nobody can open it
          again.
        </p>
      </section>
      <section>
        <h2>Your rights</h2>
        <ul>
          <li><strong>Access and portability:</strong> export notes as Markdown and your library as BibTeX or RIS from the app.</li>
          <li><strong>Correction:</strong> edit your profile and your content directly.</li>
          <li>
            <strong>Deletion:</strong> delete notebooks and projects from their settings. To delete your account entirely, write to us: everything you own goes with
            it. What you wrote in other people's notebooks and projects stays there, without your name.
          </li>
        </ul>
      </section>
      <section id="cookies" className="scroll-mt-20">
        <h2>Cookies</h2>
        <ul>
          <li>
            <strong>Needed:</strong> one cookie keeps you signed in, and one remembers your cookie choice. Your theme and layout preferences stay in your browser.
          </li>
          <li>
            <strong>Analytics, only if you accept:</strong> a cookie with a random number lets us count visits and see which pages and features of {APP_NAME} are used
            (for example “a task was added” or “a manuscript was downloaded as PDF”, and, if you are signed in, by which account), where visitors come from (the website,
            not the full address) and the kind of device. No IP address, no content, nothing shared with anyone: the counts stay in our own database and are deleted
            after 13 months.
          </li>
          <li>
            You can change your choice at any time: <em>Cookie settings</em> at the bottom of the public pages, or Settings → Privacy & security in the app. Refusing removes the
            analytics cookie. We count how many people accept or refuse, without knowing who.
          </li>
          <li>No third-party cookies.</li>
        </ul>
      </section>
      <section>
        <h2>Contact</h2>
        <p>
          Questions about your data: <a className="text-primary underline" href="mailto:contact@brainlog.net">contact@brainlog.net</a>
        </p>
      </section>
    </Page>
  );
}

export function TermsPage() {
  const paid = PLANS.filter((p) => p.id !== "free");
  return (
    <Page title="Terms of service" updated="October 2026">
      <section>
        <h2>In short</h2>
        <ul>
          <li>You own what you write and add. We store it only to run the service.</li>
          <li>You can export your work at any time and leave.</li>
          <li>Don't use {APP_NAME} for anything unlawful.</li>
          <li>You are responsible for what you share and for what AI assistants you connect do in your name.</li>
        </ul>
      </section>
      <section>
        <h2>Accounts</h2>
        <p>Anyone can create an account. Keep your password and any keys you create private: actions taken with them count as yours.</p>
      </section>
      <section>
        <h2>Plans and payment</h2>
        <ul>
          <li>
            <strong>Free:</strong> every feature, 1 GB for files, unlimited AI with your own key and a few AI actions a day included.
          </li>
          {paid.map((p) => (
            <li key={p.id}>
              <strong>{p.name}:</strong> {p.perPerson ? `€${p.yearly} per person a year` : `€${p.monthly} a month or €${p.yearly} a year`}. {p.features.join(". ")}.
            </li>
          ))}
          <li>Students get Researcher for €{STUDENT_DISCOUNT.yearly} a year (over {STUDENT_DISCOUNT.percent}% off) with a confirmed university email address.</li>
          <li>Prices include VAT. Payments are processed by Stripe.</li>
          <li>
            Included AI is for your own research work, within the monthly amount of your plan. AI used with your own key is billed to you by your AI provider, not by us.
          </li>
          <li>
            You can cancel at any time; the plan runs until the end of the period you paid for. Then the Free limits apply: nothing is deleted, and you can still read and export
            everything. Only new uploads pause while you are above 1 GB.
          </li>
        </ul>
      </section>
      <section>
        <h2>Acceptable use</h2>
        <ul>
          <li>No unlawful content, and nothing that infringes other people's rights.</li>
          <li>No attempts to access other people's data or to disrupt the service.</li>
          <li>No reselling of the service without our permission.</li>
        </ul>
      </section>
      <section>
        <h2>Your content</h2>
        <p>
          Your work is yours. You give us only the permission needed to store, display, back up and process it (including sending it to the AI service you use, when you ask)
          in order to run the service. AI suggestions can be wrong: check them before relying on them.
        </p>
      </section>
      <section>
        <h2>Availability and liability</h2>
        <p>
          We work hard to keep {APP_NAME} available and your data safe, and we make regular backups, but the service is provided as it is. Keep exports of anything you
          cannot lose. To the extent the law allows, our liability is limited to what you paid us in the last 12 months.
        </p>
      </section>
      <section>
        <h2>Changes and ending</h2>
        <p>
          You can stop at any time and ask us to delete your account. We may suspend accounts that break these terms. If these terms change in an important way, we will tell
          you in the app or by email.
        </p>
      </section>
      <section>
        <h2>Contact</h2>
        <p>
          <a className="text-primary underline" href="mailto:contact@brainlog.net">contact@brainlog.net</a>
        </p>
      </section>
    </Page>
  );
}
